import i18next from 'i18next'
import React from 'react'
import { Link } from 'react-router-dom'
import { Action, mkWidget, Widget } from 'widgets-for-react'
import { useBreakpointMatches } from '../../../hooks/useBreakpointListener'
import { HeaderState, OptionsState } from '../../public_site.main.mod'
import MainNavItems from './nav_items_main'
import { SearchBar } from './searchbar'

export const header: (_: HeaderState, o: OptionsState) => Widget<Action<HeaderState>> = (props, opts) =>
  mkWidget({
    run: (setState) => {
      return (
        <Header
          {...props}
          onToggleMobileMenu={() => setState((s) => ({ ...s, menu: s.menu == 'open' ? 'closed' : 'open' }))}
          onCloseMobileMenu={() => setState((s) => ({ ...s, menu: 'closed' }))}
          onToggleSearchBar={() => setState((s) => ({ ...s, showSearchBar: !props.showSearchBar }))}
          onCloseSearchBar={() => setState((s) => ({ ...s, showSearchBar: false }))}
          onUpdateSearchValue={(newValue) => setState((s) => ({ ...s, searchValue: newValue }))}
        />
      )
    }
  })

const Header = (
  props: HeaderState & {
    onToggleMobileMenu: () => void
    onCloseMobileMenu: () => void
    onToggleSearchBar: () => void
    onCloseSearchBar: () => void
    onUpdateSearchValue: (newValue: string) => void
  }
) => {
  const { menu } = props
  const isMenuOpen = menu === 'open'
  const isDesktop = useBreakpointMatches('up', 1440)
  
  // ----- Mobile hamburger menu state management
  React.useEffect(() => {
    const lastFocusedElement = document.activeElement
    const escapeHandler = (e: KeyboardEvent) => isMenuOpen && e.key === 'Escape' && props.onCloseMobileMenu()

    if (isDesktop) {
    } else {
      // Set class on html element to indicate menu state and prevent page scrolling
      isMenuOpen
        ? document.documentElement.classList.add('menu--open')
        : document.documentElement.classList.remove('menu--open')

      // Create focus-trap effect by setting inert attribute on main and footer
      const main = document.querySelector('#a11y-main')
      const footer = document.querySelector('#a11y-footer')
      if (isMenuOpen) {
        main?.setAttribute('inert', '')
        footer?.setAttribute('inert', '')
      } else {
        main?.removeAttribute('inert')
        footer?.removeAttribute('inert')
      }

      // Close mobile hamburger menu on Escape
      if (isMenuOpen) {
        window.addEventListener('keydown', escapeHandler, { passive: true })
      }
    }

    return () => {
      window.removeEventListener('keydown', escapeHandler)
      if (lastFocusedElement !== null && lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus()
    }
  }, [isMenuOpen, isDesktop])
  return (
    <>
      <SkipLinks />
      <header className="site-header sh">
        <div className="sh__disclaimer typo--s ff--primary">
          <span>{i18next.t('general.disclaimer')}</span>
        </div>
        <div className="sh__inner container container--m gutter--m">
          <Logo display="mobile" hidden={isDesktop} />
          <div className="sh__navs" id="a11y-menu">

            <div className="sh__navs-inner">
              <Logo display="desktop" hidden={!isDesktop} />

              <nav className="sh__nav nav nav--main" aria-label="Primary">
                <MainNavItems location="main" hasActiveState={true} />
              </nav>

              <nav className="sh__nav nav nav--top" aria-label="Secondary">
                <ul className="nav__list">
                  <MainNavItems location="top" hasActiveState={true} />
                  <li className="nav__item nav__item--top sh__show--desktop">
                    <SearchButton {...props} display="desktop" onClick={props.onToggleSearchBar} />
                  </li>
                </ul>
              </nav>
            </div>
          </div>

          <div className="sh__actions">
            <div className="sh__action">
              <SearchButton {...props} display="mobile" onClick={props.onToggleSearchBar} />
            </div>

            <div className="sh__action" hidden={isDesktop}>
              <button
                className="sh__toggler toggler"
                onClick={props.onToggleMobileMenu}
                aria-controls="a11y-menu"
                aria-expanded={props.menu == 'open'}
              >
                <span className="sr-text">{props.menu == 'open' ? 'Close menu' : 'Open menu'}</span>
                <span className="toggler__line toggler__line--close" />
                <span className="toggler__line toggler__line--close" />
                <span className="toggler__line" />
              </button>
            </div>
          </div>
        </div>
        <SearchBar
          show={props.showSearchBar}
          hide={props.onCloseSearchBar}
          searchValue={props.searchValue}
          updateSearchValue={props.onUpdateSearchValue}
        />
      </header>
    </>
  )
}

const Logo = (props: { display: 'mobile' | 'desktop'; hidden: boolean }) => (
  <Link className={`sh__logo-link sh__show--${props.display}`} to="/" hidden={props.hidden}>
    <span className="sr-text">Go to frieslandcampinainstitute.com</span>
    <svg className="sh__logo" height={88} width={164}>
      <title>FrieslandCampina Institute Logo</title>
      <use xlinkHref="#logo" />
    </svg>
  </Link>
)

const SearchButton = (props: HeaderState & { display: 'mobile' | 'desktop'; onClick: () => void }) => {
  const label = props.showSearchBar ? 'Hide Search bar' : 'Show Search bar'
  return (
    <button
      className={`search sh__search sh__show--${props.display}`}
      onClick={(_) => {
        props.onClick()
        !props.showSearchBar ? document.body.classList.add('noscroll') : document.body.classList.remove('noscroll')
      }}
      aria-haspopup="true"
      aria-expanded={props.showSearchBar ? true : false}
      title={label}
      aria-controls="a11y-searchbar"
    >
      <span className="sr-text">{label}</span>
      <svg className="search__icon" width={20} height={20} aria-hidden="true">
        <use xlinkHref="#icon--search" />
      </svg>
    </button>
  )
}

const SkipLinks = () => (
  <nav aria-label="Skip Links">
    <a href="#a11y-main" className="sr-text">
      Jump to main content
    </a>
    <a href="#a11y-footer" className="sr-text">
      Jump to footer
    </a>
  </nav>
)
